/**
 * G2 axis label overlap transforms for category (x) axes.
 *
 * Category labels in this library used to be pre-truncated in a `labelFormatter`: a pixel budget was
 * estimated for each tick, then the string was wrapped and ellipsised to fit it. That approach needs
 * two estimates to both be right — the plot width (derived from hardcoded axis and padding reserves)
 * and the rendered text width (derived from a per-glyph table) — and the per-tick budget divides the
 * plot-width error by the category count, so sparse charts amplified it most. Labels collided
 * whenever either estimate ran optimistic.
 *
 * Instead G2 lays the labels out itself, from the tick geometry the chart already guarantees:
 *
 *  - `ellipsis` installs the `...` overflow suffix (see 3 below).
 *  - `wrap` then breaks each label across up to `maxLines` lines at `wordWrapWidth`, which the caller
 *    derives from the chart's *fixed* per-category stride — a lower bound on the rendered band step,
 *    since a chart that cannot fit its categories windows or scrolls rather than compressing them.
 *    Every line therefore fits inside one tick's own space and cannot reach its neighbour, whatever
 *    the container width, the loaded font, or the label casing.
 *
 * Four G2 behaviours this encodes, all verified against `@antv/component` and `@antv/g2`:
 *
 *  1. `inferLabelOverlap` uses a non-empty `transform` array *verbatim* and ignores the
 *     `labelAutoHide` / `labelAutoWrap` / `labelAutoEllipsis` / `labelAutoRotate` flags entirely.
 *     Passing `transform` is therefore the only reliable way to control this; the flags are
 *     defaulted from the theme and a truthy `labelAutoHide` silently drops whole ticks.
 *  2. `hide` is deliberately omitted. It is what removes category labels altogether, and it also
 *     forces `crossSize: false` on the axis, changing the layout.
 *  3. **`ellipsis` must run before `wrap`, not after.** `ellipsis` shrinks labels against their real
 *     measured bounds, which sounds like the stronger guarantee, but it applies its width via
 *     `ellipsisIt`, which sets `maxLines: 1` — collapsing any wrap that ran before it back onto one
 *     line. Running it first and `wrap` second keeps the multi-line layout: `wrap` overrides the
 *     single-line collapse and the wrap width, and what survives from `ellipsis` is the
 *     `textOverflow` suffix, which `wrap` alone does not set (text past `maxLines` would clip).
 *  4. `recoverWhenFailed` must be passed as `false`. For a horizontal linear axis `wrap` applies
 *     `maxLines` once and, if the labels still overlap, *undoes* the wrap and reverts to a single
 *     line. G2's own flag-based default tries to disable this but misspells the option as
 *     `recoveryWhenFail`, so it never takes effect; spelling it correctly here is what keeps the wrap.
 */

/** Wrapped lines allowed for a vertical chart's category labels (per design guardrails). */
export const CATEGORY_AXIS_LABEL_MAX_LINES = 3;

/** Wrapped lines allowed for a horizontal chart's category labels (per design guardrails). */
export const HORIZONTAL_CATEGORY_AXIS_LABEL_MAX_LINES = 2;

/**
 * Horizontal breathing room (px) kept clear between two adjacent labels: reserved from the tick
 * space before lines are broken, and passed to `boundTest` as a left/right margin so a label that
 * does have to shrink stops at a visible gap rather than when it merely stops touching.
 */
export const CATEGORY_AXIS_LABEL_GAP_PX = 12;

/**
 * Narrowest text (px) `ellipsis` may shrink a label to before giving up. Without a floor it will
 * reduce a label towards a bare suffix, which identifies nothing; stopping early leaves a slight
 * overlap that is still more readable than three dots.
 */
export const CATEGORY_AXIS_LABEL_MIN_LENGTH_PX = 24;

/** Fallback tick space (px) when the caller has no per-category geometry to offer. */
const DEFAULT_TICK_SPACE_PX = 88;

export interface CategoryAxisLabelTransformOptions {
    /**
     * Horizontal space (px) a single tick label may occupy, used to choose where lines break. Pass
     * the chart's *fixed* per-category stride (bar width + category gap), not a stride derived from
     * an estimated plot width: the fixed stride is a lower bound on the rendered band `step`, since
     * a chart that cannot fit its categories windows or scrolls rather than shrinking the step. A
     * lower bound wraps text onto more lines than strictly necessary; an over-estimate instead lets
     * `ellipsis` cut text that would have fitted, which is the worse failure.
     *
     * Only a hint either way — `ellipsis` enforces the no-overlap guarantee.
     */
    tickSpacePx?: number;
    /** Maximum wrapped lines before the label is ellipsised instead. */
    maxLines?: number;
    /** Minimum gap in px enforced between adjacent labels. */
    gapPx?: number;
    /** Narrowest text in px a label may be shrunk to. */
    minLengthPx?: number;
}

/**
 * Build the `axis.x.transform` chain for a category axis: wrap onto multiple lines first, then
 * ellipsise only as far as needed to clear the neighbouring label.
 *
 * Set this on `axis.x.transform`. Do not also pass `labelAutoWrap` / `labelAutoHide` /
 * `labelAutoEllipsis` — a non-empty `transform` makes G2 ignore them.
 */
export function getCategoryAxisLabelTransforms(
    options: CategoryAxisLabelTransformOptions = {},
): Array<Record<string, unknown>> {
    const {
        tickSpacePx = DEFAULT_TICK_SPACE_PX,
        maxLines = CATEGORY_AXIS_LABEL_MAX_LINES,
        gapPx = CATEGORY_AXIS_LABEL_GAP_PX,
        minLengthPx = CATEGORY_AXIS_LABEL_MIN_LENGTH_PX,
    } = options;

    // Half the gap on each side of a label; two adjacent labels therefore stay `gapPx` apart.
    const sideMarginPx = Math.max(0, gapPx) / 2;

    return [
        {
            type: 'ellipsis',
            suffix: '...',
            minLength: Math.max(0, minLengthPx),
            margin: [0, sideMarginPx, 0, sideMarginPx],
        },
        {
            type: 'wrap',
            // Break lines at the tick space less the gap the labels must keep clear of each other, so
            // no line can reach into a neighbouring tick.
            wordWrapWidth: Math.max(1, tickSpacePx - Math.max(0, gapPx)),
            maxLines: Math.max(1, Math.floor(maxLines)),
            recoverWhenFailed: false,
        },
    ];
}
